// APG date-grid keyboard navigation: arrows move day/week, Home/End move week, PageUp/Dn move month/year, Enter/Space select, Escape dismiss; disabled days remain focusable.

import { useCallback } from "react";
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  type WeekStart,
} from "./dateCore";

export interface UseCalendarKeyboardOptions {
  focusedDay: Date;
  setFocusedDay: (date: Date) => void;
  selectDay: (date: Date) => void;
  weekStartsOn?: WeekStart;
  /** Outer month bounds; focus is never allowed past them. */
  navStart: Date;
  navEnd: Date;
  onEscape?: () => void;
}

export interface UseCalendarKeyboardReturn {
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

export function useCalendarKeyboard({
  focusedDay,
  setFocusedDay,
  selectDay,
  weekStartsOn = 0,
  navStart,
  navEnd,
  onEscape,
}: UseCalendarKeyboardOptions): UseCalendarKeyboardReturn {
  const moveTo = useCallback(
    (next: Date) => {
      const lowerBound = startOfMonth(navStart);
      const upperBound = endOfMonth(navEnd);
      if (next < lowerBound) {
        setFocusedDay(lowerBound);
        return;
      }
      if (next > upperBound) {
        setFocusedDay(upperBound);
        return;
      }
      setFocusedDay(next);
    },
    [setFocusedDay, navStart, navEnd],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          moveTo(addDays(focusedDay, -1));
          break;
        case "ArrowRight":
          event.preventDefault();
          moveTo(addDays(focusedDay, 1));
          break;
        case "ArrowUp":
          event.preventDefault();
          moveTo(addDays(focusedDay, -7));
          break;
        case "ArrowDown":
          event.preventDefault();
          moveTo(addDays(focusedDay, 7));
          break;
        case "Home":
          event.preventDefault();
          moveTo(startOfWeek(focusedDay, weekStartsOn));
          break;
        case "End":
          event.preventDefault();
          moveTo(endOfWeek(focusedDay, weekStartsOn));
          break;
        case "PageUp":
          event.preventDefault();
          moveTo(event.shiftKey ? addYears(focusedDay, -1) : addMonths(focusedDay, -1));
          break;
        case "PageDown":
          event.preventDefault();
          moveTo(event.shiftKey ? addYears(focusedDay, 1) : addMonths(focusedDay, 1));
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          selectDay(focusedDay);
          break;
        case "Escape":
          // No preventDefault: the popover's dismiss handler needs the event.
          onEscape?.();
          break;
        default:
          break;
      }
    },
    [focusedDay, moveTo, selectDay, weekStartsOn, onEscape],
  );

  return { onKeyDown };
}
